import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowUpRight } from 'lucide-react';
import { useSite } from '../context/SiteContext';
import { Reveal } from './Reveal';

export default function WorkGrid({ projects, filters, testIdPrefix = 'work', onFilterChange }) {
  const { t, lang } = useSite();
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState(null);

  const visible = projects.filter((p) => filter === 'all' || p.cat === filter || p.brand === filter);

  return (
    <div>
      <div className="mb-10 flex flex-wrap gap-2" role="tablist" data-testid={`${testIdPrefix}-filters`}>
        {filters.map((f) => (
          <button
            key={f.key}
            role="tab"
            aria-selected={filter === f.key}
            onClick={() => { setFilter(f.key); onFilterChange?.(f.key); }}
            data-testid={`${testIdPrefix}-filter-${f.key}`}
            className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-all duration-300 ${
              filter === f.key
                ? 'brand-border text-black !bg-[var(--brand)]'
                : 'border-zinc-800 text-zinc-400 hover:border-zinc-500 hover:text-white'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <motion.div layout className="grid grid-cols-1 gap-5 md:grid-cols-6" data-testid={`${testIdPrefix}-grid`}>
        {visible.length === 0 && (
          <p
            className="col-span-full border border-dashed border-zinc-800 p-10 text-center font-mono text-xs uppercase tracking-[0.25em] text-zinc-500"
            data-testid={`${testIdPrefix}-empty`}
          >
            {t.portfolio.empty}
          </p>
        )}
        <AnimatePresence mode="popLayout">
          {visible.map((p) => (
            <motion.button
              layout
              key={p.id}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => setOpen(p)}
              data-testid={`${testIdPrefix}-card-${p.id}`}
              data-cursor={`${t.cursor.viewProject} →`}
              className={`group image-frame text-left ${p.cls}`}
            >
              <img
                src={p.img}
                alt={p.title[lang]}
                loading="lazy"
                className="transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              <div className="absolute bottom-0 left-0 right-0 translate-y-3 p-5 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] brand-text">
                  {p.brand === 'media' ? 'F&L Media' : 'F&L Souvenir'}
                </p>
                <p className="mt-1 font-heading text-lg font-bold uppercase text-white">{p.title[lang]}</p>
              </div>
            </motion.button>
          ))}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[130] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
            onClick={() => setOpen(null)}
            role="dialog"
            aria-modal="true"
            aria-label={open.title[lang]}
            data-testid="project-modal"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-[90vh] w-full max-w-4xl overflow-y-auto border border-zinc-800 bg-zinc-950"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative aspect-[16/9] overflow-hidden">
                <img src={open.img} alt={open.title[lang]} className="h-full w-full object-cover" />
                <button
                  onClick={() => setOpen(null)}
                  aria-label={t.portfolio.close}
                  data-testid="project-modal-close"
                  className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center border border-zinc-700 bg-black/60 text-white backdrop-blur transition-colors hover:!bg-[var(--brand)] hover:text-black"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-6 sm:p-10">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] brand-text">
                  {open.brand === 'media' ? 'F&L Media' : 'F&L Souvenir'} · {open.cat}
                </p>
                <h3 className="mt-2 font-heading text-3xl font-extrabold uppercase tracking-tight text-white">
                  {open.title[lang]}
                </h3>
                <p className="mt-4 max-w-2xl leading-relaxed text-zinc-400">{open.desc[lang]}</p>
                <div className="mt-6">
                  <p className="kicker mb-2">{t.portfolio.servicesDone}</p>
                  <div className="flex flex-wrap gap-2">
                    {open.services[lang].map((s) => (
                      <span key={s} className="border border-zinc-800 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-zinc-300">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
                <p className="mt-6 border-l-2 pl-4 text-xs italic text-zinc-600 brand-border">
                  {t.portfolio.placeholderNote}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function CTABanner({ title, text, cta, to, testId, email }) {
  const { t } = useSite();
  return (
    <Reveal className="border-y border-zinc-900">
      <div className="mx-auto flex max-w-[1600px] flex-col items-start gap-6 px-5 py-20 sm:flex-row sm:items-center sm:justify-between sm:px-8 sm:py-28">
        <div>
          <h2 className="max-w-2xl font-heading text-3xl font-extrabold uppercase tracking-tight sm:text-5xl">{title}</h2>
          {text && <p className="mt-3 max-w-xl text-zinc-400">{text}</p>}
          {email && (
            <a
              href={`mailto:${email}`}
              data-testid={`${testId}-email`}
              data-cursor={t.cursor.contact}
              className="mt-4 inline-block font-mono text-xs tracking-widest text-zinc-400 underline decoration-zinc-700 underline-offset-4 transition-colors hover:text-white"
            >
              {email}
            </a>
          )}
        </div>
        <Link
          to={to}
          data-testid={testId}
          data-cursor={t.cursor.contact}
          className="group inline-flex items-center gap-3 border px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] transition-colors duration-300 brand-border hover:!bg-[var(--brand)] hover:text-black"
        >
          {cta}
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </Link>
      </div>
    </Reveal>
  );
}
